import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck, FileSearch, ClipboardCheck, Hammer, Users, Wrench, Phone, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";

const BENEFITS = [
  { i: <ClipboardCheck />, t: "Annual External Park Home Health Check", d: "Included with membership. An external visual condition review of your park home each year, with maintenance guidance." },
  { i: <FileSearch />, t: "Independent Quote Reviews", d: "Avoid overpaying or approving unnecessary work." },
  { i: <Phone />, t: "Technical Support", d: "Specialist help on the phone or by email when something goes wrong." },
  { i: <ShieldCheck />, t: "Insurance Guidance", d: "Understand what may or may not be covered before you make decisions." },
  { i: <Wrench />, t: "Maintenance Responsibility Advice", d: "Plain-English guidance on what you maintain, what the park maintains, and what insurance may cover." },
  { i: <Users />, t: "Ongoing Member Support", d: "You are not left dealing with complex issues alone." },
];

export default function SupportPlan() {
  return (
    <div data-testid="support-plan-page">
      <SEO
        title="Park Home Support Plan | Annual Health Check + Quote Reviews | £99 First Year"
        description="An annual membership for park home owners. Includes an external Park Home Health Check each year, independent quote reviews, technical support, insurance guidance and maintenance responsibility advice. £99 first year, then £249/year."
        keywords="park home support plan, park home health check, park home membership, park home support UK, park home advice service"
        canonical="https://parkhomesupport.co.uk/support-plan"
      />
      <section className="hero-gradient section-pad">
        <div className="container-phs">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="eyebrow">Annual Membership · Health Check Included</span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05] text-[#071A33]">
                More Than Support — <span className="text-[#064E2E]">A Yearly Health Check for Your Park Home.</span>
              </h1>
              <p className="mt-6 text-lg text-[#334155] max-w-xl">
                Park Home Support is your annual partner for prevention, advice and trusted second opinions. For £99 in your first year, you receive an Annual External Park Home Health Check, expert guidance before approving quotes, and year-round support on technical, maintenance and insurance-related decisions.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/membership" className="btn-primary" data-testid="sp-join-top-btn">
                  Join Park Home Support <ArrowRight size={16}/>
                </Link>
                <Link to="/quote-review" className="btn-outline" data-testid="sp-quote-btn">
                  Try Quote Review
                </Link>
              </div>
            </div>

            <div className="card-dark">
              <span className="text-[#B88A2A] tracking-[0.22em] uppercase text-xs font-medium">Park Home Support Membership</span>
              <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                <span className="price-display text-6xl">
                  <span className="price-currency">£</span>
                  <span className="price-value">99</span>
                </span>
                <span className="text-white/70">first year</span>
              </div>
              <p className="text-white/70 text-sm mt-1">Then £249 / year thereafter (auto-renew)</p>

              <ul className="mt-6 space-y-2 text-sm text-white/85">
                <li className="flex gap-2"><CheckCircle2 size={15} className="text-[#B88A2A] mt-0.5"/> <span><strong>Annual External Park Home Health Check</strong></span></li>
                <li className="flex gap-2"><CheckCircle2 size={15} className="text-[#B88A2A] mt-0.5"/> Independent Quote Reviews</li>
                <li className="flex gap-2"><CheckCircle2 size={15} className="text-[#B88A2A] mt-0.5"/> Technical Support</li>
                <li className="flex gap-2"><CheckCircle2 size={15} className="text-[#B88A2A] mt-0.5"/> Insurance Guidance</li>
                <li className="flex gap-2"><CheckCircle2 size={15} className="text-[#B88A2A] mt-0.5"/> Maintenance Responsibility Advice</li>
              </ul>

              <Link to="/membership" className="btn-accent mt-6 w-full" data-testid="support-plan-join-btn">
                Join Now — £99 First Year <ArrowRight size={16}/>
              </Link>
              <p className="text-xs text-white/60 mt-3">
                Renews at £249 per year. No refunds once service has been used.
              </p>

              <div className="mt-8 pt-6 border-t border-white/10 text-sm text-white/80 leading-relaxed">
                <p className="font-medium text-white mb-2">Cancellation & cooling-off</p>
                <p>You can cancel within 14 days if you have not used the service. Once you request support, submit a quote, receive advice or otherwise use the service, your right to cancel is waived and the membership becomes non-refundable for the current term.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-phs">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b) => (
              <div key={b.t} className="card-default" data-testid={`sp-benefit-${b.t.toLowerCase().replace(/[^a-z]+/g,"-")}`}>
                <div className="w-11 h-11 bg-[#064E2E] text-white rounded-xl flex items-center justify-center">{b.i}</div>
                <h3 className="font-display text-xl mt-5 text-[#071A33]">{b.t}</h3>
                <p className="mt-2 text-[#334155] leading-relaxed text-[15px]">{b.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 card-soft text-center" data-testid="sp-pricing-footer">
            <Phone className="mx-auto text-[#0F766E]" />
            <h3 className="font-display text-3xl mt-3 text-[#071A33]">Questions before joining?</h3>
            <p className="mt-2 text-[#334155]">Speak to us first — we're happy to talk you through what's covered.</p>
            <Link to="/contact" className="btn-outline mt-6" data-testid="support-plan-contact-btn">Contact us</Link>
            <p className="text-xs text-[#475569] mt-6 max-w-2xl mx-auto leading-relaxed">
              The Annual External Park Home Health Check is an external visual condition review with maintenance guidance — not a regulated survey, structural survey or warranty. Contractor work, additional inspections and remedial repairs are quoted separately. Membership renews at £249 per year unless cancelled before renewal.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
